import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, FileText, CheckCircle, XCircle, AlertTriangle, Eye, RefreshCw, DollarSign, Calendar, Hash, ScanLine, Zap } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function PaymentCapturePage() {
  const navigate = useNavigate();
  const [ocrStatus, setOcrStatus] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/payment-capture/ocr-status"),
      api.get("/customer-payments/pending-review?limit=50"),
      api.get("/customers?limit=500"),
    ]).then(([ocr, pend, cust]) => {
      setOcrStatus(ocr.data);
      setPending(pend.data.payments || []);
      setCustomers(cust.data.customers || []);
    }).catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="payment-capture-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Payment Capture</h1>
          <p className="text-sm text-gray-500 mt-1">Upload and process proof of payment documents via OCR</p>
        </div>
        <Button data-testid="upload-pop-btn" onClick={() => setUploadDialog(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Upload className="w-4 h-4 mr-1.5" />Upload POP
        </Button>
      </div>

      {/* OCR Status */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ocrStatus?.ocr_available ? "bg-green-100" : "bg-amber-100"}`}>
              <ScanLine className={`w-5 h-5 ${ocrStatus?.ocr_available ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">OCR Engine</div>
              <div className="text-sm font-medium">{ocrStatus?.provider || "Loading..."}</div>
              <Badge className={`rounded-full text-xs mt-0.5 ${ocrStatus?.ocr_available ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"} border`}>
                {ocrStatus?.ocr_available ? "Active" : "Not Configured"}
              </Badge>
            </div>
          </CardContent>
        </Card>
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">Pending Review</div>
              <div className="text-2xl font-bold text-[#1F2937]">{pending.length}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">How It Works</div>
              <div className="text-xs text-gray-500 mt-0.5">Upload POP &rarr; OCR extracts details &rarr; Review & approve</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Review List */}
      <h2 className="text-lg font-semibold text-[#1F2937] mb-3">Pending Review ({pending.length})</h2>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : pending.length === 0 ? (
        <Card className="border rounded-sm">
          <CardContent className="p-12 text-center text-gray-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No payments pending review</p>
            <p className="text-xs mt-1">Upload a proof of payment to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pending.map(p => (
            <PendingPaymentCard key={p.payment_id} payment={p} customers={customers} onRefresh={load} />
          ))}
        </div>
      )}

      <UploadDialog open={uploadDialog} onClose={() => setUploadDialog(false)} customers={customers} onSuccess={() => { setUploadDialog(false); load(); }} />
    </div>
  );
}

function PendingPaymentCard({ payment, customers, onRefresh }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [approveForm, setApproveForm] = useState({ amount: payment.amount || 0, payment_date: payment.payment_date || "", reference: payment.reference || "", customer_id: payment.customer_id || "", allocations: [] });
  const [rejectReason, setRejectReason] = useState("");
  const [actioning, setActioning] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const ocr = payment.ocr_data || {};
  const suggested = payment.suggested_allocations || [];

  const reprocess = async () => {
    setReprocessing(true);
    try {
      await api.post(`/payment-capture/${payment.payment_id}/reprocess-ocr`);
      toast.success("OCR reprocessed"); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setReprocessing(false); }
  };

  const approve = async () => {
    setActioning(true);
    try {
      await api.post(`/customer-payments/${payment.payment_id}/approve-review`, approveForm);
      toast.success("Payment approved"); setApproveDialog(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const reject = async () => {
    setActioning(true);
    try {
      await api.post(`/customer-payments/${payment.payment_id}/reject-review`, { reason: rejectReason });
      toast.success("Payment rejected"); setRejectDialog(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  useEffect(() => {
    if (approveDialog && suggested.length > 0 && approveForm.allocations.length === 0) {
      const amount = approveForm.amount || payment.amount || 0;
      let remaining = amount;
      const allocs = [];
      for (const s of suggested) {
        if (remaining <= 0.01) break;
        const allocAmt = Math.min(remaining, s.balance);
        if (allocAmt > 0) {
          allocs.push({ invoice_id: s.invoice_id, amount: allocAmt });
          remaining -= allocAmt;
        }
      }
      setApproveForm(f => ({ ...f, allocations: allocs }));
    }
  }, [approveDialog]);

  const confidenceColor = ocr.confidence === "high" ? "text-green-600 bg-green-50 border-green-200" : ocr.confidence === "medium" ? "text-amber-600 bg-amber-50 border-amber-200" : "text-red-600 bg-red-50 border-red-200";

  return (
    <>
      <Card data-testid={`pending-card-${payment.payment_id}`} className="border rounded-sm hover:shadow-sm transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[#E63946]">{payment.payment_number}</span>
                <Badge className="rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">Pending Review</Badge>
                {payment.imported_via_email && <Badge className="rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200">Email Import</Badge>}
                {payment.manual_upload && <Badge className="rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">Manual Upload</Badge>}
                {ocr.success && <Badge className={`rounded-full text-xs border ${confidenceColor}`}>OCR: {ocr.confidence || "low"} ({ocr.confidence_score || 0}%)</Badge>}
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /><strong>{fmt(payment.amount || 0)}</strong></span>
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{payment.payment_date || "No date"}</span>
                <span className="flex items-center gap-1"><Hash className="w-3.5 h-3.5" />{payment.reference || "No ref"}</span>
                <span>{payment.customer_name || "Unknown customer"}</span>
              </div>
              {suggested.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {suggested.slice(0, 3).map((s, i) => (
                    <Badge key={i} className={`rounded-full text-xs border ${s.confidence === "high" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
                      {s.invoice_number} ({fmt(s.balance)}) - {s.match_reason}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-1.5 ml-4">
              <Button data-testid={`reprocess-${payment.payment_id}`} size="sm" variant="outline" className="rounded-sm" onClick={reprocess} disabled={reprocessing}>
                {reprocessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setExpanded(!expanded)}>
                <Eye className="w-4 h-4" />
              </Button>
              <Button data-testid={`approve-${payment.payment_id}`} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={() => setApproveDialog(true)}>
                <CheckCircle className="w-4 h-4 mr-1" />Approve
              </Button>
              <Button data-testid={`reject-${payment.payment_id}`} size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRejectDialog(true)}>
                <XCircle className="w-4 h-4 mr-1" />Reject
              </Button>
            </div>
          </div>
          {/* Expanded OCR Details */}
          {expanded && (
            <div className="mt-4 pt-4 border-t space-y-3">
              {ocr.raw_text && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">OCR Extracted Text</div>
                  <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded max-h-40 overflow-y-auto whitespace-pre-wrap font-mono">{ocr.raw_text}</pre>
                </div>
              )}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-xs"><span className="text-gray-500">Extracted Amount:</span><br /><strong>{ocr.extracted_amount ? fmt(ocr.extracted_amount) : "Not detected"}</strong></div>
                <div className="text-xs"><span className="text-gray-500">Extracted Date:</span><br /><strong>{ocr.extracted_date || "Not detected"}</strong></div>
                <div className="text-xs"><span className="text-gray-500">Extracted Ref:</span><br /><strong>{ocr.extracted_reference || "Not detected"}</strong></div>
                <div className="text-xs"><span className="text-gray-500">Bank Ref:</span><br /><strong>{ocr.extracted_bank_reference || "Not detected"}</strong></div>
              </div>
              {ocr.warnings?.length > 0 && (
                <div className="flex flex-wrap gap-1">{ocr.warnings.map((w, i) => (
                  <Badge key={i} className="rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">{w}</Badge>
                ))}</div>
              )}
              {(payment.pop_files || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Attached Files</div>
                  <div className="flex gap-2">{payment.pop_files.map((f, i) => (
                    <Badge key={i} className="rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-200">
                      <FileText className="w-3 h-3 mr-1" />{f.filename} ({Math.round(f.size / 1024)}KB)
                    </Badge>
                  ))}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Approve Payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Amount</label>
                <Input data-testid="approve-amount" type="number" step="0.01" value={approveForm.amount} onChange={(e) => setApproveForm({ ...approveForm, amount: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Date</label>
                <Input type="date" value={approveForm.payment_date} onChange={(e) => setApproveForm({ ...approveForm, payment_date: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Reference</label>
              <Input value={approveForm.reference} onChange={(e) => setApproveForm({ ...approveForm, reference: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">Invoice Allocations</label>
              {suggested.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No outstanding invoices found for this customer. Payment will be recorded as unallocated.</p>
              ) : suggested.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2 text-sm">
                    <Badge className={`rounded-full text-xs ${s.confidence === "high" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-600"} border`}>{s.match_reason}</Badge>
                    <span className="font-medium">{s.invoice_number}</span>
                    <span className="text-gray-500">({fmt(s.balance)} due)</span>
                    <Input type="number" step="0.01" className="w-28 h-8 text-xs ml-auto" value={approveForm.allocations.find(a => a.invoice_id === s.invoice_id)?.amount || ""} onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      const allocs = [...approveForm.allocations.filter(a => a.invoice_id !== s.invoice_id)];
                      if (val > 0) allocs.push({ invoice_id: s.invoice_id, amount: val });
                      setApproveForm({ ...approveForm, allocations: allocs });
                    }} />
                  </div>
                ))}
              </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-approve-btn" onClick={approve} disabled={actioning || !approveForm.amount} className="bg-green-600 hover:bg-green-700 text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}Approve & Allocate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Payment</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Rejection Reason</label>
            <textarea className="w-full rounded-sm border p-2.5 text-sm" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Why is this payment being rejected?" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-reject-btn" onClick={reject} disabled={actioning} className="bg-red-600 hover:bg-red-700 text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UploadDialog({ open, onClose, customers, onSuccess }) {
  const [customerId, setCustomerId] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
    setResults([]);
  };

  const upload = async () => {
    if (!customerId) return toast.error("Select a customer");
    if (files.length === 0) return toast.error("Select files to upload");
    setUploading(true);
    setResults([]);
    const uploadResults = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("customer_id", customerId);
      formData.append("file", file);
      try {
        const res = await api.post("/payment-capture/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        uploadResults.push({ filename: file.name, success: true, payment: res.data });
      } catch (e) {
        uploadResults.push({ filename: file.name, success: false, error: e.response?.data?.detail || "Upload failed" });
      }
    }
    setResults(uploadResults);
    setUploading(false);
    const successCount = uploadResults.filter(r => r.success).length;
    if (successCount > 0) {
      toast.success(`${successCount} of ${files.length} files processed`);
      setTimeout(onSuccess, 1500);
    } else {
      toast.error("All uploads failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Upload Proof of Payment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Customer</label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="upload-customer-select"><SelectValue placeholder="Select customer..." /></SelectTrigger>
              <SelectContent>{customers.map(c => (
                <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">POP Documents</label>
            <div className="border-2 border-dashed rounded-sm p-6 text-center hover:border-[#E63946] transition-colors cursor-pointer" onClick={() => document.getElementById("pop-file-input").click()}>
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">{files.length > 0 ? `${files.length} file(s) selected` : "Click to select PDF, JPG, or PNG files"}</p>
              <p className="text-xs text-gray-400 mt-1">Supported: PDF, JPG, PNG, TIFF (max 20MB each)</p>
            </div>
            <input id="pop-file-input" data-testid="pop-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp" className="hidden" onChange={handleFileChange} />
            {files.length > 0 && (
              <div className="mt-2 space-y-1">{files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <FileText className="w-3.5 h-3.5" />{f.name} ({Math.round(f.size / 1024)}KB)
                </div>
              ))}</div>
            )}
          </div>
          {results.length > 0 && (
            <div className="space-y-1">{results.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs p-2 rounded ${r.success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {r.success ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                <span>{r.filename}</span>
                {r.success && r.payment && <span className="ml-auto">{r.payment.payment_number} - {fmt(r.payment.amount)} (OCR: {r.payment.ocr_data?.confidence || "N/A"})</span>}
                {!r.success && <span className="ml-auto">{r.error}</span>}
              </div>
            ))}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="confirm-upload-btn" onClick={upload} disabled={uploading || files.length === 0 || !customerId} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1" />}Upload & Process OCR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
